import { TriangleAlertIcon } from 'lucide-react';
import * as React from 'react';

import {
  DimeAlert,
  DimeAlertDescription,
  DimeAlertTitle,
  type DimeAlertVariant,
} from './DimeAlert.js';
import {
  DimeCard,
  DimeCardAction,
  DimeCardContent,
  DimeCardDescription,
  DimeCardFooter,
  DimeCardHeader,
  DimeCardTitle,
} from './DimeCard.js';
import { DimeCardShellMeta, type DimeCardShellMetaItem } from './DimeCardShellMeta.js';
import { buildActivityMetaItems } from './lib/build-activity-meta-items.js';
import { cn } from './lib/utils.js';

export type DimeCardShellBanner = {
  /** Omit to always show; `false` drops the banner, so callers pass a condition inline. */
  when?: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  variant?: DimeAlertVariant;
  /** Defaults to a warning triangle; pass a node to override, or `null` for none. */
  icon?: React.ReactNode;
};

export type DimeCardShellActivity = Readonly<{
  created?: string | null;
  createdBy?: string | null;
  updated?: string | null;
  updatedBy?: string | null;
}>;

type DimeCardShellProps = Readonly<
  // `content` is also a DOM attribute (typed string), so the slot names are dropped before the
  // card's own props are merged in.
  Omit<React.ComponentProps<typeof DimeCard>, 'children' | 'content' | 'title'> & {
    /** Renders above the banners and header, e.g. a DimeActionToolbar. */
    toolbar?: React.ReactNode;
    banners?: DimeCardShellBanner[];
    header?: React.ReactNode;
    title?: React.ReactNode;
    /** Wraps the title (not the status chips) in a heading of this level; omit for a plain title. */
    headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
    status?: React.ReactNode;
    description?: React.ReactNode;
    meta?: DimeCardShellMetaItem[];
    /** Free-form metrics under the facts row (e.g. a KPI strip); `meta` stays the preset. */
    metaContent?: React.ReactNode;
    activity?: DimeCardShellActivity;
    actions?: React.ReactNode;
    content?: React.ReactNode;
    footer?: React.ReactNode;
    /** Keeps the footer on screen at the bottom while a long page scrolls, without a separator, e.g. a create form's Save / Cancel. */
    stickyFooter?: boolean;
    /** Per-part classes, merged after the shell's own. */
    classNames?: Readonly<Partial<Record<'footer', string>>>;
  }
>;

export function DimeCardShell({
  toolbar,
  banners,
  header,
  title,
  headingLevel,
  status,
  description,
  meta,
  metaContent,
  activity,
  actions,
  content,
  footer,
  borderless = true,
  stickyFooter = false,
  classNames = {},
  className,
  ...props
}: DimeCardShellProps) {
  const activityItems = buildActivityMetaItems(activity);
  const Heading = headingLevel === undefined ? undefined : (`h${headingLevel}` as const);

  const hasHeader =
    title !== undefined ||
    status !== undefined ||
    description !== undefined ||
    actions !== undefined ||
    metaContent !== undefined ||
    (meta?.length ?? 0) > 0 ||
    activityItems.length > 0;

  return (
    // The banners sit inside the card so their spacing comes from the card's own gap -- a wrapper
    // around both would need a margin that survives an absent banner.
    <DimeCard
      borderless={borderless}
      className={cn('min-w-0', borderless && 'py-0', className)}
      {...props}
    >
      {toolbar}
      {/* WHY: keyed by position in the source array, not by title -- a ReactNode title stringifies
          to "[object Object]", colliding across banners, and the filtered index shifts when a
          `when` flips. */}
      {banners
        ?.map((banner, index) => ({ banner, key: index }))
        .filter(({ banner }) => banner.when !== false)
        .map(({ banner, key }) => (
          <DimeAlert key={key} variant={banner.variant ?? 'warning'}>
            {banner.icon === undefined ? <TriangleAlertIcon /> : banner.icon}
            <DimeAlertTitle>{banner.title}</DimeAlertTitle>
            {banner.description ? (
              <DimeAlertDescription>{banner.description}</DimeAlertDescription>
            ) : null}
          </DimeAlert>
        ))}
      {header !== undefined ? (
        header
      ) : hasHeader ? (
        <DimeCardHeader className={cn(borderless && 'px-0')}>
          <DimeCardTitle className="flex flex-wrap items-center gap-2 text-base">
            {Heading && title !== undefined ? <Heading>{title}</Heading> : title}
            {status}
          </DimeCardTitle>
          {description ? (
            <DimeCardDescription className="text-[15px]">{description}</DimeCardDescription>
          ) : null}
          {meta && meta.length > 0 ? (
            <DimeCardShellMeta items={meta} label="Facts" className="col-start-1" />
          ) : null}
          {metaContent ? <div className="col-start-1">{metaContent}</div> : null}
          {activityItems.length > 0 ? (
            <DimeCardShellMeta items={activityItems} label="Activity" className="col-start-1" />
          ) : null}
          {actions ? <DimeCardAction className="flex gap-2">{actions}</DimeCardAction> : null}
        </DimeCardHeader>
      ) : null}
      {content ? (
        <DimeCardContent
          className={cn(
            borderless && 'px-0',
            // A field focused near the bottom scrolls clear of the sticky footer rather than under it.
            stickyFooter && '**:scroll-mb-16',
          )}
        >
          {content}
        </DimeCardContent>
      ) : null}
      {footer ? (
        <DimeCardFooter
          className={cn(
            borderless && 'px-0',
            // The page-colored ring hides a hairline seam Chrome leaves at a sticky layer's edge.
            stickyFooter &&
              'sticky bottom-0 z-10 rounded-none border-t-0 bg-background py-3 ring-2 ring-background scroll-fade-above',
            classNames.footer,
          )}
        >
          {footer}
        </DimeCardFooter>
      ) : null}
    </DimeCard>
  );
}
